import { SupportedLanguageCode } from "./index";

export const VALID_LANG_PATHS = ["jp", "us", "cn"] as const;
export type LangPath = (typeof VALID_LANG_PATHS)[number];

export const PATH_TO_LANG: Record<LangPath, SupportedLanguageCode> = {
  jp: "ja",
  us: "en",
  cn: "zh",
};

export const LANG_TO_PATH: Record<SupportedLanguageCode, LangPath> = {
  ja: "jp",
  en: "us",
  zh: "cn",
};

export const DEFAULT_LANG_PATH: LangPath = "jp";

export function isValidLangPath(path: string | undefined): path is LangPath {
  return VALID_LANG_PATHS.includes(path as LangPath);
}

export function getLangFromPath(path: string | undefined): SupportedLanguageCode {
  if (isValidLangPath(path)) {
    return PATH_TO_LANG[path];
  }
  return PATH_TO_LANG[DEFAULT_LANG_PATH];
}

export function getPathFromLang(lang: string | undefined): LangPath {
  const code = (lang || "").split("-")[0] as SupportedLanguageCode;
  if (code in LANG_TO_PATH) {
    return LANG_TO_PATH[code];
  }
  return DEFAULT_LANG_PATH;
}

/**
 * 検出されたユーザーの言語設定（localStorage または navigator）から初期のパスプレフィックスを決定
 */
export function getDefaultLangPath(): LangPath {
  if (typeof window === "undefined") {
    return DEFAULT_LANG_PATH;
  }

  const savedLng = window.localStorage?.getItem("i18nextLng");
  if (savedLng) {
    const code = savedLng.split("-")[0].toLowerCase();
    if (code === "zh") return "cn";
    if (code === "en") return "us";
    if (code === "ja") return "jp";
  }

  const navLangs = [
    ...(window.navigator?.languages || []),
    window.navigator?.language,
  ].filter(Boolean) as string[];

  for (const raw of navLangs) {
    const l = raw.toLowerCase();
    if (l.startsWith("ja")) return "jp";
    if (l.startsWith("zh")) return "cn";
    if (l.startsWith("en")) return "us";
  }

  return DEFAULT_LANG_PATH;
}

/**
 * パス名から言語プレフィックスを取り除いた正規パスを返す
 * 例: /jp/service -> /service, /us -> /
 */
export function stripLangPrefix(pathname: string): string {
  if (!pathname || pathname === "/") return "/";
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length > 0 && isValidLangPath(segments[0])) {
    const rest = segments.slice(1).join("/");
    return rest ? `/${rest}` : "/";
  }
  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

/**
 * コラムページ（多言語非対応・プレフィックス不要）かどうかを判定
 */
export function isColumnPath(pathname: string): boolean {
  const clean = stripLangPrefix(pathname);
  return clean === "/column" || clean.startsWith("/column/") || clean.startsWith("/column?");
}

/**
 * 現在のパスまたは指定パスを、指定された言語プレフィックス付きのパスに変換する
 * ※コラム（/column）は言語プレフィックスを付与しない
 */
export function getLocalizedPath(to: string, langPath: LangPath): string {
  if (!to) return `/${langPath}`;
  if (
    to.startsWith("http://") ||
    to.startsWith("https://") ||
    to.startsWith("tel:") ||
    to.startsWith("mailto:")
  ) {
    return to;
  }

  // ハッシュやクエリの分離
  const [pathPart, queryOrHash = ""] = to.split(/([?#].*)/, 2);
  const cleanPath = stripLangPrefix(pathPart);

  // コラムはプレフィックスなしを維持
  if (cleanPath === "/column" || cleanPath.startsWith("/column/")) {
    return `${cleanPath}${queryOrHash}`;
  }

  const localizedBasePath = cleanPath === "/" ? `/${langPath}/` : `/${langPath}${cleanPath}`;
  return `${localizedBasePath}${queryOrHash}`;
}

/**
 * 現在のURLの言語プレフィックスのみを切り替える
 */
export function switchLangPath(pathname: string, targetLangPath: LangPath): string {
  const cleanPath = stripLangPrefix(pathname);

  // コラムページで他言語に切り替えた場合は各言語のトップへ、日本語なら /column のまま
  if (cleanPath === "/column" || cleanPath.startsWith("/column/")) {
    if (targetLangPath === "jp") {
      return cleanPath;
    }
    return `/${targetLangPath}/`;
  }

  if (cleanPath === "/") {
    if (targetLangPath === "jp") {
      // 方式2: 日本語の場合は / または /jp/
      return pathname.startsWith("/jp") ? "/jp/" : "/";
    }
    return `/${targetLangPath}/`;
  }

  return `/${targetLangPath}${cleanPath}`;
}
